import { Component, OnDestroy, OnInit, HostListener } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatButton, MatLabel } from 'src/app/shared/material';
import { DrawerService } from 'src/app/shared/services';
import {
  ControlType,
  DynamicFormControl,
  DynamicFormControlComponent,
  DynamicFormGroup,
  FormGroupFactory,
} from 'src/app/shared/forms';
import { FilterService } from '../../services/filter.service';
import { ActiveFilter } from '../../models/active-filter.model';
import { ActiveFilterFactory } from '../../services/active-filter-factory.service';
import { forEach } from 'lodash';

@Component({
    selector: 'filter-drawer',
    imports: [
        MatButton,
        MatLabel,
        ReactiveFormsModule,
        DynamicFormControlComponent,
    ],
    templateUrl: './filter-drawer.component.html',
    styleUrl: './filter-drawer.component.scss'
})
export class FilterDrawerComponent implements OnInit, OnDestroy {
  ControlType = ControlType;
  formGroup!: FormGroup;
  controlGroups: DynamicFormGroup[] = [];
  controls: DynamicFormControl[] = [];

  private _subscriptions: Subscription[] = [];

  constructor(
    private formBuilder: FormBuilder,
    public service: FilterService,
    private drawerSvc: DrawerService,
    private groupFact: FormGroupFactory,
    private afFact: ActiveFilterFactory,
  ) { }

  ngOnInit(): void {
    this.setFilters(this.service.filterFormControls);
    let sub = this.service.filterFormControlChanges$.subscribe((filters) => {
      this.setFilters(filters);
    });
    this._subscriptions.push(sub);

    // track active filter changes
    sub = this.service.activeFilterChanges$.subscribe(() => {
      this.setFilters(this.service.filterFormControls);
    });
    this._subscriptions.push(sub);

    sub = this.drawerSvc.filterDrawerStateChanged$.subscribe(() => {
      this.setFilters(this.service.filterFormControls);
    });
    this._subscriptions.push(sub);
  }

  ngOnDestroy() {
    this._subscriptions.forEach((i) => i.unsubscribe());
  }

  apply() {
    let activeFilters: ActiveFilter[] = [];
    this.controls.forEach((i) => {
      switch (i.controlType) {
        case ControlType.MULTISELECT:
          activeFilters = [
            ...activeFilters,
            ...this.getMultiSelectActiveFilters(i),
          ];
          break;
        case ControlType.DATERANGE:
          activeFilters = [
            ...activeFilters,
            ...this.getDateRangeActiveFilters(i),
          ];
          break;
        case ControlType.SELECT:
          activeFilters = [...activeFilters, ...this.getSelectActiveFilter(i)];
          break;
        default:
          let filters = this.formGroup.controls[i.propertyBinding].value
            ? [
              this.afFact.createActiveFilter(
                i.controlType,
                i.label,
                this.formGroup.controls[i.propertyBinding].value,
                i.propertyBinding,
              ),
            ]
            : [];

          activeFilters = [...activeFilters, ...filters];
      }
    });

    this.service.setActiveFilters(activeFilters);
    this.drawerSvc.closeFilter();
  }

  resetFilter(groupName: string) {
    this.controls
      .filter((i) => i.customOptions.groupName === groupName)
      .flatMap((i) => i.formControls)
      .forEach((ctrl) => {
        ctrl.formControl.setValue(null);
      });
  }

  onResetAll(): void {
    this.controls
      .map((i) => i.customOptions.groupName)
      .forEach((i) => {
        this.resetFilter(i);
      });
  }

  getControls(group: DynamicFormGroup): DynamicFormControl[] {
    let controls = group.getControls();
    return controls;
  }

  private buildFormGroup() {
    this.formGroup = this.formBuilder.group([]);
    this.setTextBoxControls(
      this.controls.filter((i) => i.controlType === ControlType.INPUT),
    );
    this.setDateRangeControls(
      this.controls.filter((i) => i.controlType === ControlType.DATERANGE),
    );
    this.setMultiSelectControls(this.addMultiSelectCustomOptions());
  }

  private addMultiSelectCustomOptions(): DynamicFormControl[] {
    let multiSelectFilters = this.controls.filter(
      (i) => i.controlType === ControlType.MULTISELECT,
    );

    // This prevents manual text entry in multi select form fields
    return multiSelectFilters.map((i) => {
      i.customOptions = {
        ...i.customOptions,
        textInputEnabled: false,
      };
      return i;
    });
  }

  private setTextBoxControls(controls: DynamicFormControl[]): void {
    controls
      .flatMap((i) => i.formControls)
      .forEach((ctrl) => {
        let actives = this.service.activeFilters?.filter((i) =>
          i.propertyBinding.includes(ctrl.propertyBinding),
        )[0];

        if (actives) {
          ctrl.formControl.setValue(actives.value);
        }
        this.formGroup.addControl(ctrl.propertyBinding, ctrl.formControl);
      });
  }

  private setDateRangeControls(controls: DynamicFormControl[]): void {
    controls.forEach((ctrl) => {
      let actives = this.service.activeFilters?.filter((i) =>
        i.propertyBinding.includes(ctrl.propertyBinding),
      )[0];

      ctrl.formControls.forEach((i) => {
        if (actives && i.propertyBinding.toLowerCase().includes('start')) {
          i.formControl.setValue(actives.value?.start);
        }
        if (actives && i.propertyBinding.toLowerCase().includes('end')) {
          i.formControl.setValue(actives.value?.end);
        }
        this.formGroup.addControl(i.propertyBinding, i.formControl);
      });
    });
  }

  private setMultiSelectControls(controls: DynamicFormControl[]): void {
    controls.forEach((ctrl) => {
      let actives = this.service.activeFilters?.filter((i) =>
        i.propertyBinding.includes(ctrl.propertyBinding),
      );
      ctrl.formControls.forEach((i) => {
        if (!i.propertyBinding.includes('autocomplete')) {
          i.formControl.setValue(actives?.map((i) => i.value));
        }
        this.formGroup.addControl(i.propertyBinding, i.formControl);
      });
    });
  }

  private buildFilterGroup(
    heading?: string,
    controls?: DynamicFormControl[],
    handler?: Function,
  ): DynamicFormGroup {
    let group = this.groupFact.createFormGroup(heading, 'row', {}, controls);
    group.addHandler('on_reset_click', handler);
    return group;
  }

  private buildDynamicFormGroups() {
     let definedGroups = [
      {
        name: 'Text Filters',
        controls: this.controls.filter(
          (i) => i.customOptions.groupName === 'textFilters',
        ),
        handler: () => {
          this.resetFilter('textFilters');
        },
      },
      {
        name: 'Multi-Select Filters',
        controls: this.controls.filter(
          (i) =>
            i.customOptions.groupName === 'selectFilters',
        ),
        handler: () => {
          this.resetFilter('selectFilters');
        },
      },
      {
        name: 'Date Filters',
        controls: this.controls.filter(
          (i) =>
            i.customOptions.groupName === 'dateFilters'
        ),
        handler: () => {
          this.resetFilter('dateFilters');
        },
      },
      {
        name: 'Customer Information Filters',
        controls: this.controls.filter(
          (i) =>
            i.customOptions.groupName === 'customerInfo'
        ),
        handler: () => {
          this.resetFilter('customerInfo');
        },
      },
    ];

    definedGroups.forEach((i) => {
      if (i.controls.length > 0) {
        this.controlGroups = [
          ...this.controlGroups,
          this.buildFilterGroup(i.name, i.controls, i.handler),
        ];
      }
    });
  }

  private setFilters(filters: DynamicFormControl[]) {
    this.controls = filters;
    this.controlGroups = [];
    this.buildDynamicFormGroups();
    this.buildFormGroup();
  }

  private getDateRangeActiveFilters(
    control: DynamicFormControl,
  ): ActiveFilter[] {
    let startValue = control.formControls.filter((i) =>
      i.propertyBinding.includes('start'),
    )[0].formControl.value;
    let endValue = control.formControls.filter((i) =>
      i.propertyBinding.includes('end'),
    )[0].formControl.value;

    return startValue && endValue
      ? [
        this.afFact.createActiveFilter(
          control.controlType,
          control.label,
          { start: startValue, end: endValue },
          control.propertyBinding,
        ),
      ]
      : [];
  }

  private getMultiSelectActiveFilters(
    control: DynamicFormControl,
  ): ActiveFilter[] {
    let selectOptions = control.customOptions.selectOptions;
    let formControl = control.formControls.find(
      (i) => !i.propertyBinding.includes('autocomplete'),
    ).formControl;

    return (
      formControl.value?.map((i: string[]) => {
        return this.afFact.createActiveFilter(
          control.controlType,
          control.label,
          i,
          control.propertyBinding,
          selectOptions.find((opt: any) => opt.label === i)?.value,
        );
      }) || []
    );
  }

  private getSelectActiveFilter(control: DynamicFormControl): ActiveFilter[] {
    let formControl = control.formControls.find(
      (i) => !i.propertyBinding.includes('autocomplete'),
    ).formControl;
    const formLabel = control.customOptions.selectOptions.find(
      (x: any) => x.value === formControl.value,
    )?.label;
    return (
      formControl.value ? [
        this.afFact.createActiveFilter(
          control.controlType,
          control.label,
          formControl.value,
          control.propertyBinding,
          formLabel,
        ),
      ] : []
    )
  }
}
